import { PAYMENT_STATUSES } from "@/sanctuary/typedefs";

const RECEIPT_ELIGIBLE_STATUSES = [
  PAYMENT_STATUSES.SUCCESSFUL,
  PAYMENT_STATUSES.REFUNDED,
];

// Print without the images that have not loaded by then, rather than never printing.
export const IMAGE_LOAD_TIMEOUT_MS = 5000;

// Email receipts rely on background colors, which browsers drop when printing by default.
const PRINT_STYLES = `
  @page { margin: 0.4in; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

export const isReceiptEligible = (status) =>
  RECEIPT_ELIGIBLE_STATUSES.includes(status);

const imageSettled = (image) =>
  new Promise((resolve) => {
    image.addEventListener("load", resolve, { once: true });
    image.addEventListener("error", resolve, { once: true });
  });

const imagesLoaded = (doc) =>
  Promise.all(
    Array.from(doc.images).filter((image) => !image.complete).map(imageSettled)
  );

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);

export const printHtmlDocument = async (
  { title, html },
  { print = (frameWindow) => frameWindow.print() } = {}
) => {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position: fixed; width: 0; height: 0; border: 0;";
  document.body.appendChild(frame);

  const frameDocument = frame.contentDocument;
  frameDocument.open();
  frameDocument.write(html);
  frameDocument.close();
  frameDocument.title = title;
  const style = frameDocument.createElement("style");
  style.textContent = PRINT_STYLES;
  frameDocument.head.appendChild(style);

  // Chrome names the saved PDF after the top-level page title.
  const pageTitle = document.title;
  document.title = title;
  const cleanUp = () => {
    document.title = pageTitle;
    frame.remove();
  };
  frame.contentWindow.addEventListener("afterprint", cleanUp, { once: true });

  try {
    await withTimeout(imagesLoaded(frameDocument), IMAGE_LOAD_TIMEOUT_MS);
    frame.contentWindow.focus();
    print(frame.contentWindow);
  } catch (error) {
    cleanUp();
    throw error;
  }
};
